import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/feature/SEOSchema";
import { aboutData } from "@/mocks/about";

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const breadcrumbItems = [
    { label: "首页", path: "/" },
    { label: "关于我们" },
  ];

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于17c一起草官网入口",
    description: aboutData.brand.description,
    url: "https://17c-yiqicao.com/about",
    about: {
      "@type": "Organization",
      name: "17c一起草官网入口",
      description: aboutData.brand.description,
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("phone_alt") as string || "").trim();

    if (honeypot) {
      setFormSuccess(true);
      form.reset();
      setSubmitting(false);
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9pholdi2vhsdh2d34ng", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || "提交失败，请稍后重试";
        setFormError(serverMsg.includes("spam") ? "提交失败，请稍后重试" : serverMsg);
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOSchema schemas={[aboutSchema]} />
      <Navbar />
      <main>
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 pt-8 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {aboutData.brand.title}
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {aboutData.brand.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              <div className="p-6 rounded-2xl border border-background-200/70 bg-background-50">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-focus-3-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.brand.mission}</p>
              </div>
              <div className="p-6 rounded-2xl border border-background-200/70 bg-background-50">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-lg text-accent-600"></i>
                </div>
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.brand.vision}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {aboutData.timeline.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className="md:w-1/2 flex md:justify-end items-center pl-12 md:pl-0 md:pr-10">
                      <div className={`${index % 2 === 0 ? "md:text-right" : "md:text-left md:pl-10"}`}>
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-primary-100 text-primary-700 mb-1">
                          {item.year}
                        </span>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-0 md:left-1/2 top-1 w-8 h-8 md:-ml-4 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">
              {aboutData.reputation.title}
            </h2>
            <p className="text-sm text-foreground-600 mb-10 text-center">
              {aboutData.reputation.subtitle}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {aboutData.reputation.items.map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl border border-background-200/70 bg-background-50"
                >
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-secondary-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full py-16 bg-background-100">
          <div className="max-w-xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3 text-center">
              {aboutData.contact.title}
            </h2>
            <p className="text-sm text-foreground-600 mb-10 text-center">
              {aboutData.contact.subtitle}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-4 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-950">{aboutData.contact.email}</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">客服电话</p>
                <p className="text-sm font-medium text-foreground-950">{aboutData.contact.phone}</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-secondary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">工作时间</p>
                <p className="text-sm font-medium text-foreground-950">{aboutData.contact.workingHours}</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              id="contact-form"
              className="bg-background-50 rounded-2xl border border-background-200/70 p-6"
            >
              {formSuccess && (
                <div className="mb-4 p-4 rounded-xl bg-accent-50 border border-accent-200">
                  <p className="text-sm text-accent-800 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    感谢您的留言，我们会尽快回复您！
                  </p>
                </div>
              )}

              {formError && (
                <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <div className="anti-spam-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>

              <div className="mb-5">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {submitting ? "提交中..." : "发送留言"}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}